import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePostHog } from "posthog-js/react";

// What each event means is written up in suggestionTracking.md; keep the two in step.
export const SUGGESTION_EVENTS = {
  LIST_SHOWN: "suggestions_list_shown",
  SEEN: "suggestion_seen",
  REASON_OPENED: "suggestion_reason_opened",
  ACCOUNTS_EXPANDED: "suggestion_accounts_expanded",
  SHOW_MORE_CLICKED: "suggestions_show_more_clicked",
  SEND_CLICKED: "suggestion_send_clicked",
  PREVIEW_OPENED: "suggestion_preview_opened",
  TASK_FORM_OPENED: "suggestion_task_form_opened",
  MARKED_DONE: "suggestion_marked_done",
  DISMISSED: "suggestion_dismissed",
  COMPLETED: "suggestion_completed",
  ABANDONED: "suggestion_abandoned",
  FAILED: "suggestion_failed",
  TIME_SPENT: "suggestions_time_spent",
};

const SUGGESTION_TYPES = {
  SendPaymentRequest: "payment_request",
  SendDocuments: "documents",
  CreateTask: "task",
};

const SEEN_VISIBLE_RATIO = 0.5;
const SEEN_AFTER_MS = 1000;

const hoursSince = (iso) => (iso ? Math.round((Date.now() - new Date(iso).getTime()) / 360_000) / 10 : null);
const secondsSince = (ms) => (ms == null ? null : Math.round((performance.now() - ms) / 100) / 10);
const canObserve = () => typeof IntersectionObserver !== "undefined";

/**
 * Returns a ref for a suggestion row; `onSeen` fires once the row has been at least half on screen for a second.
 */
export const useSeenOnce = (onSeen) => {
  const ref = useRef(null);
  const latest = useRef(onSeen);
  latest.current = onSeen;

  useEffect(() => {
    const el = ref.current;
    if (!el || !canObserve()) return undefined;
    let timer = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= SEEN_VISIBLE_RATIO) {
          timer ??= setTimeout(() => {
            observer.disconnect();
            latest.current?.();
          }, SEEN_AFTER_MS);
        } else {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, SEEN_VISIBLE_RATIO] },
    );
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return ref;
};

/**
 * PostHog tracking for a plan of action (Home's "Suggested for today" or a working set's plan). `steps` are the
 * plan's steps in the order shown, so each event can say where in the list the suggestion sat.
 *
 * Returns `listRef` for the list's outer element (time on screen is counted while it's in view and the tab is
 * visible), `trackStep(event, step, extra)`, `track(event, extra)` and `stepSeen(step)`.
 */
export const useSuggestionAnalytics = ({ plan, location, steps }) => {
  const posthog = usePostHog();
  const planRef = useRef(plan);
  planRef.current = plan;
  const seenAt = useRef(new Map());
  const visit = useRef({ acted: 0, dismissed: 0 });

  const positions = useMemo(() => new Map(steps.map((step, i) => [step.key, i + 1])), [steps]);
  const positionsRef = useRef(positions);
  positionsRef.current = positions;

  const capture = useCallback(
    (event, properties, options) => posthog?.capture(event, { location, ...properties }, options),
    [posthog, location],
  );

  const trackStep = useCallback(
    (event, step, extra) => {
      const single = step.members.length === 1 ? step.members[0] : null;
      if (event === SUGGESTION_EVENTS.DISMISSED) visit.current.dismissed += 1;
      if (event === SUGGESTION_EVENTS.COMPLETED || event === SUGGESTION_EVENTS.MARKED_DONE) visit.current.acted += 1;
      capture(event, {
        suggestion_type: SUGGESTION_TYPES[step.kind] ?? step.kind,
        category: step.category,
        template_id: step.templateId ?? null,
        template_name: step.templateName ?? null,
        account_count: step.members.length,
        overdue_amount: step.totalOverdue,
        position_in_list: positionsRef.current.get(step.key) ?? null,
        hours_since_generated: hoursSince(planRef.current?.generatedAt),
        step_key: step.key,
        suggestion_id: single?.suggestionId ?? null,
        customer_id: single?.customerId ?? null,
        ...extra,
      });
    },
    [capture],
  );

  const secondsSinceSeen = useCallback((step) => secondsSince(seenAt.current.get(step.key)), []);

  const stepSeen = useCallback(
    (step) => {
      if (seenAt.current.has(step.key)) return;
      seenAt.current.set(step.key, performance.now());
      trackStep(SUGGESTION_EVENTS.SEEN, step);
    },
    [trackStep],
  );

  // Once per mount: refetches and local done/dismiss marks change the steps but aren't a new showing.
  const shown = useRef(false);
  useEffect(() => {
    if (!plan || shown.current) return;
    shown.current = true;
    const counts = plan.counts;
    capture(SUGGESTION_EVENTS.LIST_SHOWN, {
      suggestion_count: steps.length,
      open_count: counts?.open ?? null,
      done_count: counts?.done ?? null,
      dismissed_count: counts?.dismissed ?? null,
      changed_since_generated_count: counts?.changedSinceGenerated ?? null,
      is_empty: steps.length === 0,
      hours_since_generated: hoursSince(plan.generatedAt),
    });
  }, [plan, steps, capture]);

  const listEl = useRef(null);
  const observer = useRef(null);
  const listRef = useCallback((el) => {
    if (listEl.current) observer.current?.unobserve(listEl.current);
    listEl.current = el;
    if (el) observer.current?.observe(el);
  }, []);

  useEffect(() => {
    let onScreen = false;
    let since = null;
    let totalMs = 0;

    const update = () => {
      const visible = onScreen && document.visibilityState === "visible";
      if (visible && since == null) since = performance.now();
      if (!visible && since != null) {
        totalMs += performance.now() - since;
        since = null;
      }
    };
    const flush = (options) => {
      if (since != null) {
        totalMs += performance.now() - since;
        since = performance.now();
      }
      if (totalMs < 1000) return;
      capture(
        SUGGESTION_EVENTS.TIME_SPENT,
        {
          seconds_on_screen: Math.round(totalMs / 1000),
          suggestions_seen: seenAt.current.size,
          suggestions_acted_on: visit.current.acted,
          suggestions_dismissed: visit.current.dismissed,
        },
        options,
      );
      totalMs = 0;
    };

    if (canObserve()) {
      observer.current = new IntersectionObserver(([entry]) => {
        onScreen = entry.isIntersecting;
        update();
      });
      if (listEl.current) observer.current.observe(listEl.current);
    }
    const onPageHide = () => flush({ transport: "sendBeacon" });
    document.addEventListener("visibilitychange", update);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      observer.current?.disconnect();
      observer.current = null;
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("pagehide", onPageHide);
      flush();
    };
  }, [capture]);

  return { listRef, track: capture, trackStep, stepSeen, secondsSinceSeen };
};
